import type { PayloadOf, TypedEvent } from '$domain/events';
import type { Reducer } from '$domain/reducers/reduce';

/**
 * Current settings, derived from the log.
 *
 * Settings live in the event log and **only** there (architecture §2.3). v1.2
 * had them in the log *and* in the key-value store, which is two authoritative
 * copies of one fact — and would have produced a silent divergence the first
 * time two devices merged. Last write wins by ULID order, which for settings is
 * exactly the semantics you want and costs nothing to replay.
 */
export type SettingValue = string | number | boolean | null;
export type Settings = Readonly<Record<string, SettingValue>>;

export const settingsReducer: Reducer<Settings> = {
  name: 'settings',
  initial: {},
  handles: ['settings.changed'],
  apply(state, event) {
    const { key, value } = (event as TypedEvent<'settings.changed'>)
      .payload as PayloadOf<'settings.changed'>;
    return { ...state, [key]: value };
  },
};

export function getSetting<T extends SettingValue>(
  settings: Settings,
  key: string,
  fallback: T,
): T | SettingValue {
  return Object.hasOwn(settings, key) ? (settings[key] as SettingValue) : fallback;
}
